import React, { useState, useEffect, useCallback } from 'react';
import ScGrid from '../ScGrid';
import ScModal from '../ScModal';
import ScSpinner from '../ScSpinner';
import { fetchOperations, createOperation, updateOperation } from '../../studyConfigurationApi';
import { DEFAULT_OPERATIONS } from '../../studyConfigurationDefaults';

const codeKey = (code) => (code ?? '').trim();

const OperationsTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchOperations();
      setRows(res.data || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load operations.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      await updateOperation(form.operationCode, form);
      setModalOpen(false);
      setSuccess(null);
      load();
    } catch (e) {
      setError(e.response?.data?.error || 'Save failed.');
    }
  };

  const seedDefaultOperations = async () => {
    setError(null);
    setSuccess(null);
    const existing = new Set(rows.map((r) => codeKey(r.operationCode)));
    const missing = DEFAULT_OPERATIONS.filter(
      (o) => !existing.has(codeKey(o.operationCode))
    );
    const alreadyCount = DEFAULT_OPERATIONS.length - missing.length;

    if (missing.length === 0) {
      setSuccess('All default operations already exist.');
      return;
    }

    setSeeding(true);
    let inserted = 0;
    let failed = 0;
    let lastError = null;

    for (const op of missing) {
      try {
        await createOperation({
          operationCode: op.operationCode,
          operationName: op.operationName,
          operationPath: op.operationPath,
          parentOperationCode: op.parentOperationCode,
          seqNo: op.seqNo,
        });
        inserted += 1;
      } catch (e) {
        failed += 1;
        lastError = e.response?.data?.error || 'Insert failed.';
      }
    }

    setSeeding(false);
    await load();

    if (inserted > 0) {
      const parts = [`Inserted ${inserted} operation${inserted === 1 ? '' : 's'}.`];
      if (alreadyCount > 0) parts.push(`${alreadyCount} already existed.`);
      if (failed > 0) parts.push(`${failed} failed.`);
      setSuccess(parts.join(' '));
      if (failed > 0 && lastError) setError(lastError);
    } else if (lastError) {
      setError(lastError);
    }
  };

  if (loading) return <ScSpinner />;
  return (
    <div>
      {error && <div className="sc-alert sc-alert-error">{error}</div>}
      {success && <div className="sc-alert sc-alert-success">{success}</div>}
      <div className="sc-toolbar">
        <button
          type="button"
          className="sc-btn sc-btn-secondary"
          onClick={seedDefaultOperations}
          disabled={seeding}
        >
          {seeding ? 'Inserting…' : 'Insert Default Operations'}
        </button>
      </div>
      <ScGrid
        columns={[
          { key: 'operationCode', label: 'Code' },
          { key: 'operationName', label: 'Name' },
          { key: 'operationPath', label: 'Path' },
          { key: 'parentOperationCode', label: 'Parent' },
          { key: 'seqNo', label: 'Seq' },
        ]}
        rows={rows}
        onEdit={(r) => {
          if (!r.isProtected) {
            setForm({ ...r });
            setModalOpen(true);
          } else {
            setError('This operation is protected.');
          }
        }}
      />
      {form && (
        <ScModal open={modalOpen} title={`Edit ${form.operationCode}`} onClose={() => setModalOpen(false)}
          footer={<><button type="button" className="sc-btn sc-btn-secondary" onClick={() => setModalOpen(false)}>Cancel</button><button type="button" className="sc-btn sc-btn-primary" onClick={save}>Save</button></>}>
          <div className="sc-form-row"><label>Name</label><input value={form.operationName} onChange={(e) => setForm({ ...form, operationName: e.target.value })} /></div>
          <div className="sc-form-row"><label>Path</label><input value={form.operationPath || ''} onChange={(e) => setForm({ ...form, operationPath: e.target.value })} /></div>
          <div className="sc-form-row"><label>Parent Code</label><input value={form.parentOperationCode || ''} onChange={(e) => setForm({ ...form, parentOperationCode: e.target.value })} /></div>
          <div className="sc-form-row"><label>Sequence</label><input type="number" value={form.seqNo} onChange={(e) => setForm({ ...form, seqNo: Number(e.target.value) })} /></div>
        </ScModal>
      )}
    </div>
  );
};

export default OperationsTab;
